import React, { useState, useEffect, useContext } from 'react';
import { ButtonGroup, Button } from '@shopify/polaris';
import styled from 'styled-components';
import {
  TicketClient,
  AccountClient,
  GET_TASK_LIST,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION,
  UPDATE_TASK
} from '@nile/graphql';
import {
  AppContext,
  KanbanLoading,
  toast,
  ERROR_CODES
} from '@nile/components';
import { SuperBoard } from '@nile/components/src/react-super-kanban';
import { KanbanTaskCard } from '@nile/projects/src/Components/Cards';
import { KanbanAddCard } from '@nile/projects/src/Components/Inputs';
import { LaneHeader } from '@nile/projects/src/Components/Headers';
import { cloneDeep, reverse } from 'lodash';

import { FormattedMessage } from 'react-intl';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px;
  border-bottom: 1px solid #ececec;
  background-color: white;
`;

const StyledText = styled.div`
  text-transform: uppercase;
  margin: 0 30px;
`;

const KanbanViewContainer = (props) => {
  const appContext = useContext(AppContext);
  const { user } = appContext;

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('technician');
  console.log(props.project);
  const [technicianList, setTechnicianList] = useState(
    props?.project?.members || []
  );
  const [milestoneList, setMilestoneList] = useState(
    props?.project?.milestones || []
  );
  const [statusList, setStatusList] = useState(props?.statuses || []);
  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [orderedLanes, setOrderedLanes] = useState([]);

  const setOrder = (from, to) => {
    let updatedOrderedLanes = [];
    if (orderedLanes?.length) {
      updatedOrderedLanes = [...orderedLanes];
    } else {
      updatedOrderedLanes = kanbanLanes.map((lane) => lane.laneId);
    }
    const removedItem = updatedOrderedLanes.splice(from, 1);
    updatedOrderedLanes.splice(to, 0, removedItem?.[0]);
    AccountClient.mutate({
      mutation: SAVE_PERSONALIZATION,
      variables: {
        user: { userId: user.userId },
        category: `projects${props.project.projectId}`,
        name: `kanban${activeTab}`,
        value: updatedOrderedLanes
      }
    }).then((res) => {
      if (res?.data?.savePersonalization) {
        setOrderedLanes(updatedOrderedLanes);
      }
    });
  };

  const setSortedLanes = (lanes, hideLoading) => {
    AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      variables: {
        userId: user.userId,
        category: `projects${props.project.projectId}`,
        name: `kanban${activeTab}`
      },
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getPersonalizationByCategoryAndName?.value) {
        const fetchedLanes = res.data.getPersonalizationByCategoryAndName.value
          .replace('[', '')
          .replace(']', '')
          .replace(/,\s/g, ',')
          .split(',') || [...orderedLanes];

        if (fetchedLanes?.length) {
          let updatedKanbanLanes = cloneDeep(lanes);
          const reversedOrderedLanes = reverse([...fetchedLanes]);
          reversedOrderedLanes.forEach((id) => {
            const currLane = updatedKanbanLanes.filter(
              (klane) => id === klane.laneId
            );
            if (currLane.length) {
              updatedKanbanLanes = [
                ...currLane,
                ...updatedKanbanLanes.filter((klane) => id !== klane.laneId)
              ];
            }
          });
          setKanbanLanes(updatedKanbanLanes);
        } else {
          setKanbanLanes(lanes);
        }
      } else {
        setKanbanLanes(lanes);
      }
      if (!hideLoading) {
        setIsLoading(false);
      }
    });
  };

  const ROW_COUNT = 20;

  const fetchTasks = async (id, params = { page: 1, pageSize: ROW_COUNT }) => {
    const attributeMap = {
      technician: 'technician.userId',
      milestones: 'milestone.milestoneId',
      tasks: 'status'
    };

    const getOperandsList = () => {
      if (id) {
        return [
          {
            attribute: 'project.projectId',
            operator: 'is',
            value: props.project.projectId
          },
          {
            attribute: attributeMap[activeTab],
            operator: 'is',
            value: id
          }
        ];
      }
      return [
        {
          attribute: 'project.projectId',
          operator: 'is',
          value: props.project.projectId
        },
        {
          attribute: attributeMap[activeTab],
          operator: 'isEmpty',
          value: null
        }
      ];
    };
    const res = await TicketClient.query({
      query: GET_TASK_LIST,
      variables: {
        listInfo: {
          page: params.page,
          pageSize: params.pageSize,
          condition: {
            joinOperator: 'AND',
            operands: getOperandsList()
          }
        }
      },
      fetchPolicy: 'network-only'
    });
    if (res?.data?.getTasks?.tasks) {
      return {
        cards: res?.data?.getTasks?.tasks.map((task) => ({
          ...task,
          id: task.taskId
        })),
        count: res?.data?.getTasks?.listInfo.totalCount
      };
    }
    return [];
  };

  const handlePagination = async (requestedPage, laneId) => {
    const currentLaneIndex = kanbanLanes.findIndex(
      (el) => el.laneId === laneId
    );
    const currentLane = kanbanLanes[currentLaneIndex];
    const totalCount = currentLane.count;
    if ((requestedPage - 1) * ROW_COUNT < totalCount) {
      const id =
        laneId === 'Other Tasks' || laneId === 'Unassociated Tasks'
          ? null
          : laneId;
      const ticketResponse = await fetchTasks(id, {
        page: requestedPage,
        pageSize: ROW_COUNT
      });
      return ticketResponse.cards;
    }
    return [];
  };

  const constructTechnicianLanes = async (hideLoading) => {
    const promises = technicianList.map(async (technician) => {
      const cards = await fetchTasks(technician.user.userId);
      console.log(cards);
      return {
        ...technician.user,
        id: technician.user.userId,
        laneId: technician.user.userId,
        title: `${technician.user.firstName} ${technician.user.lastName}`,
        cards: cards.cards,
        count: cards.count
      };
    });
    // add owner as well
    const ownerCards = await fetchTasks(props.project.owner.userId);
    const lanes = await Promise.allSettled(promises).then((res) => {
      return res
        .filter((pr) => pr.status === 'fulfilled')
        .map((pr) => pr.value);
    });
    lanes.push({
      ...props.project.owner,
      id: props.project.owner.userId,
      laneId: props.project.owner.userId,
      title: `${props.project.owner.firstName} ${props.project.owner.lastName}`,
      cards: ownerCards.cards,
      count: ownerCards.count,
      isOwner: true
    });
    // add unassociated tasks as well
    const otherTasksCards = await fetchTasks();
    if (otherTasksCards?.cards?.length > 0) {
      lanes.push({
        id: 'Unassociated Tasks',
        laneId: 'Unassociated Tasks',
        title: 'Unassociated Tasks',
        cards: otherTasksCards.cards,
        count: otherTasksCards.count,
        isUnassociatedTasks: true
      });
    }
    setSortedLanes(lanes, hideLoading);
  };

  const constructMilestoneLanes = async (hideLoading) => {
    const promises = milestoneList.map(async (milestone) => {
      const cards = await fetchTasks(milestone.milestoneId);
      console.log(cards);
      return {
        ...milestone,
        id: milestone.milestoneId,
        laneId: milestone.milestoneId,
        title: milestone.name,
        cards: cards.cards,
        count: cards.count
      };
    });
    // add other tasks as well
    const otherTasksCards = await fetchTasks();
    const lanes = await Promise.allSettled(promises).then((res) => {
      return res
        .filter((pr) => pr.status === 'fulfilled')
        .map((pr) => pr.value);
    });
    if (otherTasksCards?.cards?.length > 0) {
      lanes.push({
        id: 'Other Tasks',
        laneId: 'Other Tasks',
        title: 'Other Tasks',
        cards: otherTasksCards.cards,
        count: otherTasksCards.count,
        isOtherTasks: true
      });
    }
    setSortedLanes(lanes, hideLoading);
  };

  const constructStatusLanes = async (hideLoading) => {
    const promises = statusList.map(async (status) => {
      const cards = await fetchTasks(status.name);
      console.log(cards);
      return {
        ...status,
        id: status.name,
        laneId: status.name,
        title: status.name,
        cards: cards.cards,
        count: cards.count
      };
    });
    const lanes = await Promise.allSettled(promises).then((res) => {
      return res
        .filter((pr) => pr.status === 'fulfilled')
        .map((pr) => pr.value);
    });
    setSortedLanes(lanes, hideLoading);
  };

  const constructionMap = {
    technician: constructTechnicianLanes,
    milestones: constructMilestoneLanes,
    tasks: constructStatusLanes
  };

  const setLanes = (hideLoading) => {
    if (!hideLoading) {
      setIsLoading(true);
    }
    constructionMap[activeTab](hideLoading);
  };

  const updateTaskHandler = (taskId, source, target) => {
    const updateObjectMap = {
      technician:
        target === 'Unassociated Tasks'
          ? { technician: null }
          : { technician: { userId: target } },
      milestones:
        target === 'Other Tasks'
          ? { milestone: null }
          : { milestone: { milestoneId: target } },
      tasks: { status: target }
    };
    if (source !== target) {
      TicketClient.mutate({
        mutation: UPDATE_TASK,
        variables: {
          taskId: taskId,
          module: 'PROJECT',
          input: updateObjectMap[activeTab]
        }
      })
        .then((res) => {
          if (res?.data?.updateTask) {
            toast('success', 'Task updated successfully');
          } else {
            let updatedLanes = kanbanLanes.filter((el, idx) => idx !== -1);
            updatedLanes = updatedLanes.map((el) => {
              return {
                ...el,
                id: `${Number(el.id) + updatedLanes.length}`
              };
            });
            setKanbanLanes(updatedLanes);
          }
        })
        .catch((err) => {
          let updatedLanes = kanbanLanes.filter((el, idx) => idx !== -1);
          updatedLanes = updatedLanes.map((el) => {
            return {
              ...el,
              id: `${Number(el.id) + updatedLanes.length}`
            };
          });
          setKanbanLanes(updatedLanes);
        });
    }
  };

  useEffect(() => {
    setTechnicianList(props.project.members);
  }, [props.project.members]);

  useEffect(() => {
    setMilestoneList(props.project.milestones);
  }, [props.project.milestones]);

  useEffect(() => {
    setLanes();
  }, [technicianList, milestoneList]);

  useEffect(() => {
    setOrderedLanes([]);
    setLanes();
  }, [activeTab]);

  const getListItems = async (params) => {
    const { list } = params;
    const projectResponse = await fetchTasks(list.laneId);
    return new Promise((resolve) => {
      resolve(
        {
          items: projectResponse.cards,
          params: { count: projectResponse.count }
        } || { items: [] }
      );
    });
  };

  return (
    <>
      <HeaderContainer data-qa="kanbanViews">
        <StyledText>
          <FormattedMessage id="project.kanban.view.header" />{' '}
        </StyledText>
        <ButtonGroup>
          <Button
            monochrome
            outline={activeTab === 'technician'}
            plain={activeTab !== 'technician'}
            onClick={() => setActiveTab('technician')}
          >
            <div data-qa="kanbanTechnician">
              <FormattedMessage id="project.kanban.navbar.tech" />
            </div>
          </Button>
          <Button
            monochrome
            outline={activeTab === 'tasks'}
            plain={activeTab !== 'tasks'}
            onClick={() => setActiveTab('tasks')}
          >
            <div data-qa="kanbanTasks">
              <FormattedMessage id="project.kanban.navbar.tasks" />
            </div>
          </Button>
          <Button
            monochrome
            outline={activeTab === 'milestones'}
            plain={activeTab !== 'milestones'}
            onClick={() => setActiveTab('milestones')}
          >
            <div data-qa="kanbanMilestones">
              <FormattedMessage id="project.kanban.navbar.milestone" />
            </div>
          </Button>
        </ButtonGroup>
      </HeaderContainer>
      {isLoading ? (
        <KanbanLoading />
      ) : (
        <SuperBoard
          lists={kanbanLanes}
          getListItems={getListItems}
          displayLaneCount={10}
          components={{
            Card: (otherProps) => (
              <KanbanTaskCard
                setLanes={setLanes}
                activeTab={activeTab}
                project={props.project}
                {...otherProps}
              />
            ),
            AddCardLink: () => null,
            LaneHeader: (otherProps) => (
              <LaneHeader
                {...otherProps}
                setLanes={setLanes}
                project={props.project}
                setProject={props.setProject}
              />
            )
          }}
          handleLaneDragEnd={(removedIndex, addedIndex, payload) =>
            setOrder(removedIndex, addedIndex)
          }
          handleDragEnd={(
            cardId,
            sourceLaneId,
            targetLaneId,
            position,
            cardDetails
          ) => {
            updateTaskHandler(cardId, sourceLaneId, targetLaneId);
          }}
          onLaneScroll={(requestedPage, laneId, board) => {
            console.log(requestedPage, laneId);
            const newCards = handlePagination(requestedPage, laneId, board);
            return new Promise((resolve) => {
              resolve(newCards);
            });
          }}
        />
      )}
    </>
  );
};

export default KanbanViewContainer;
